// @ts-nocheck
/* eslint-disable react/prop-types */

/**
 * ListNode — Card with scrollable entity list + optional selector.
 *
 * Designed to cover the same use case as legacy MultiEntityNode — a node that
 * holds a grouped, scrollable list of clickable entities with per-item tooltips
 * and per-item context menus.
 *
 * Supports:
 *   data.items          — { [groupName]: [{ label, icon, color, tooltip, id, menuItems }] }
 *   data.onItemClick    — (item, isSelected) => void
 *   data.tooltip        — string | ReactNode (for the overall node)
 *   data.menuItems      — SolyticsMenus item array (node-level right-click menu)
 *
 * Per-item props:
 *   item.label          — display text
 *   item.icon           — ReactNode rendered inside the colour circle
 *   item.color          — background colour of the icon circle (theme tokens supported)
 *   item.tooltip        — string | ReactNode shown on hover over this item
 *   item.menuItems      — SolyticsMenus item array for per-item right-click context menu
 *   item.id             — optional stable key; falls back to index
 *
 * Degrades to CircleNode when only one group with one item.
 */
import React, { memo, useState, useCallback } from 'react';
import { Position } from 'reactflow';
import { useTheme } from '@mui/material/styles';
import SolyticsBox from '../../../SolyticsBox';
import SolyticsCard from '../../../SolyticsCard';
import SolyticsTypography from '../../../SolyticsTypography';
import SolyticsDropdownInput from '../../../SolyticsDropdownInput';
import SolyticsList from '../../../SolyticsList';
import NodeHandles from '../shared/NodeHandles';
import NodeTooltip from '../shared/NodeTooltip';
import NodeMenu from '../shared/NodeMenu';
import CircleNode from './CircleNode';
import { resolveThemeColor } from '../shared/themeColorUtils';

const DEFAULT_HANDLES = [
  { type: 'target', position: Position.Left, id: 'target' },
  { type: 'source', position: Position.Right, id: 'source' },
];

const ListNode = memo(({ id, data, parentId }) => {
  const theme = useTheme();

  const items = data?.items ?? {};
  const groups = Object.keys(items).sort((a, b) => a.localeCompare(b));

  const [selectedGroup, setSelectedGroup] = useState(groups[0] ?? '');
  const [selectedIds, setSelectedIds] = useState([]);

  const activeGroup = groups.includes(selectedGroup)
    ? selectedGroup
    : (groups[0] ?? '');
  const currentItems = items[activeGroup] ?? [];

  const handleGroupChange = useCallback((e) => {
    setSelectedGroup(e.target.value);
    setSelectedIds([]);
  }, []);

  const toggleItem = useCallback(
    (item, key) => {
      setSelectedIds((prev) => {
        const isSelected = prev.includes(key);
        data?.onItemClick?.(item, !isSelected);
        return isSelected ? prev.filter((k) => k !== key) : [...prev, key];
      });
    },
    [data]
  );

  // Degrade to CircleNode when there is exactly 1 group with 1 item
  if (groups.length === 1 && currentItems.length === 1) {
    const single = currentItems[0];
    return (
      <CircleNode
        id={id}
        parentId={parentId}
        data={{
          label: single.label,
          icon: single.icon,
          color: single.color,
          tooltip: single.tooltip ?? data?.tooltip,
          menuItems: data?.menuItems,
        }}
      />
    );
  }

  return (
    <NodeMenu menuItems={data?.menuItems} nodeId={id} parentId={parentId}>
      <SolyticsBox position="relative">
        <NodeHandles
          handles={data?.handles}
          fallbackHandles={DEFAULT_HANDLES}
        />

        <NodeTooltip tooltip={data?.tooltip}>
          <SolyticsCard
            width={200}
            maxHeight={260}
            bgcolor={resolveThemeColor(
              theme,
              data?.bgColor ?? data?.color,
              'background.paper'
            )}
            elevation={2}
          >
            <SolyticsBox
              m={-2}
              mb={-3}
              display="flex"
              flexDirection="column"
              py={1}
              gap={0.5}
            >
              {groups.length > 1 && (
                <SolyticsBox
                  className="nodrag nopan"
                  width="100%"
                  mb={1}
                  px={2}
                >
                  <SolyticsDropdownInput
                    name="group"
                    size="small"
                    options={groups.map((g) => ({ label: g, value: g }))}
                    value={activeGroup}
                    onChange={handleGroupChange}
                  />
                </SolyticsBox>
              )}

              <SolyticsBox flex={1} minHeight={0} overflow="auto" paddingX={2}>
                <SolyticsList
                  dense
                  disablePadding
                  items={currentItems.map((item, idx) => {
                    const key = `${item.label}_${item.id ?? idx}`;
                    return {
                      id: key,
                      selected: selectedIds.includes(key),
                      onClick: () => toggleItem(item, key),
                      custom: (
                        <NodeMenu
                          key={key}
                          menuItems={item.menuItems}
                          nodeId={key}
                        >
                          <NodeTooltip placement="right" tooltip={item.tooltip}>
                            <SolyticsBox
                              display="flex"
                              alignItems="center"
                              width="100%"
                              onClick={() => toggleItem(item, key)}
                            >
                              <SolyticsBox
                                width={24}
                                height={24}
                                borderRadius="50%"
                                bgcolor={resolveThemeColor(
                                  theme,
                                  item.bgColor ?? item.color,
                                  'grey.30'
                                )}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                flexShrink={0}
                                mr={1}
                              >
                                {item.icon}
                              </SolyticsBox>
                              <SolyticsTypography
                                color={resolveThemeColor(
                                  theme,
                                  item.textColor,
                                  'text.primary'
                                )}
                                noWrap
                              >
                                {item.label}
                              </SolyticsTypography>
                            </SolyticsBox>
                          </NodeTooltip>
                        </NodeMenu>
                      ),
                    };
                  })}
                />
              </SolyticsBox>
            </SolyticsBox>
          </SolyticsCard>
        </NodeTooltip>
      </SolyticsBox>
    </NodeMenu>
  );
});
ListNode.displayName = 'ListNode';

export default ListNode;
